import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { Topbar } from "@/components/layout/topbar";
import { ErrorBoundary } from "@/components/error-boundary";
import { FiltersProvider } from "@/lib/filters";
import { DitherFade } from "@/components/ui/decorative-patterns";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "prado-pulse-sidebar-collapsed";

/** Remounts the error boundary on route change so navigating away clears a
 *  prior error; retry resets the query cache. */
function RouteErrorReset({ reset, children }: { reset: () => void; children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary key={pathname} onReset={reset}>
      {children}
    </ErrorBoundary>
  );
}

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Mobile drawer: lock the page scroll while open, close on Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  function toggleCollapsed() {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? "0" : "1");
      } catch {
        /* private mode — collapse for this session only */
      }
      return !c;
    });
  }

  return (
    <FiltersProvider>
      <div className="flex min-h-svh w-full bg-background">
        {/* Desktop: fixed ticketbalie-style rail */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:block">
          <AppSidebar collapsed={collapsed} />
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              aria-hidden
              className="drawer-backdrop absolute inset-0 bg-black/40"
              onClick={() => setMobileOpen(false)}
            />
            <div className="drawer-panel absolute left-0 top-0 h-full w-64 max-w-[85vw] overflow-hidden bg-card shadow-xl">
              <AppSidebar
                collapsed={false}
                onNavigate={() => setMobileOpen(false)}
                className="border-r-0"
              />
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex min-h-svh w-full min-w-0 flex-1 flex-col",
            collapsed ? "lg:pl-16" : "lg:pl-64",
          )}
        >
          <Topbar
            onToggleSidebar={toggleCollapsed}
            onOpenMobileNav={() => setMobileOpen(true)}
          />
          <main className="relative flex-1 overflow-x-clip px-4 py-6 sm:px-6 md:px-8">
            {/* Green dither band dissolving top-down */}
            <DitherFade className="h-[240px]" />
            <div className="relative z-10 mx-auto w-full max-w-7xl space-y-4">
              <QueryErrorResetBoundary>
                {({ reset }) => (
                  <RouteErrorReset reset={reset}>
                    <Outlet />
                  </RouteErrorReset>
                )}
              </QueryErrorResetBoundary>
            </div>
          </main>
        </div>
      </div>
    </FiltersProvider>
  );
}
